import { QueryClient, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/src/api";

import {
  UserAuctionsBadgesAtom,
  userAuctionsBadgesAtom
} from "@/src/state/userAuctionsBadgesAtom";

import { useClearAuctionState } from "@/src/hooks/useClearAuctionState";
import { ConsumerQueryKeys } from "@/src/queries/consumer/consumerFunctions";
import { removeAuction } from "@/src/queries/removeAuction";
import { ConsumerAuctionDTO } from "@/src/types/types";

import { useSetAtom } from "jotai";

const DRAFT_QUERY_KEY = [ConsumerQueryKeys.AUCTIONS + "/draft"] as const;
const AUCTIONS_QUERY_KEY = [ConsumerQueryKeys.AUCTIONS] as const;

export const isAuctionAlreadyRemovedError = (error: unknown): boolean => {
  if (error instanceof ApiError) {
    return error.status === 404;
  }
  if (
    typeof error === "object" &&
    error !== null &&
    "status" in error &&
    typeof (error as { status: unknown }).status === "number"
  ) {
    return (error as { status: number }).status === 404;
  }
  return false;
};

interface ClearConsumerDraftAfterRemovalParams {
  queryClient: QueryClient;
  deletedAuctionId: number;
  setUserAuctionsBadges: (
    update: (
      prev: UserAuctionsBadgesAtom | undefined
    ) => UserAuctionsBadgesAtom | undefined
  ) => void;
  clearAllAuctionState: () => void;
  clearWizardState?: boolean;
}

/** Strip deleted auction from list cache and clear draft query immediately. */
export const clearConsumerDraftAfterRemoval = async (
  params: ClearConsumerDraftAfterRemovalParams
): Promise<void> => {
  const {
    queryClient,
    deletedAuctionId,
    setUserAuctionsBadges,
    clearAllAuctionState,
    clearWizardState = true
  } = params;

  queryClient.setQueryData<ConsumerAuctionDTO[]>(
    AUCTIONS_QUERY_KEY,
    (current) => {
      if (!current) {
        return current;
      }
      return current.filter((auction) => auction.id !== deletedAuctionId);
    }
  );

  // The draft query stores raw data from the /auctions endpoint (an array),
  // not the select-ed single draft object. Filter out the deleted auction
  // instead of using removeQueries, which would wipe the cache entry and
  // cause isLoading:true when the query is re-observed (big loader flash).
  const cachedDraftData =
    queryClient.getQueryData<ConsumerAuctionDTO[]>(DRAFT_QUERY_KEY);
  if (cachedDraftData?.some((a) => a.id === deletedAuctionId)) {
    queryClient.setQueryData(
      DRAFT_QUERY_KEY,
      cachedDraftData.filter((a) => a.id !== deletedAuctionId)
    );
  }

  setUserAuctionsBadges((prev) => ({
    ...prev,
    draftAuctionId:
      prev?.draftAuctionId === deletedAuctionId
        ? undefined
        : prev?.draftAuctionId
  }));

  if (clearWizardState) {
    clearAllAuctionState();
  }

  await Promise.all([
    queryClient.invalidateQueries({ queryKey: AUCTIONS_QUERY_KEY }),
    queryClient.invalidateQueries({ queryKey: DRAFT_QUERY_KEY })
  ]);
};

interface RemoveConsumerAuctionParams {
  auctionId: number;
  queryClient: QueryClient;
  setUserAuctionsBadges: ClearConsumerDraftAfterRemovalParams["setUserAuctionsBadges"];
  clearAllAuctionState: () => void;
  clearWizardState?: boolean;
}

/** Delete auction via API and run full client draft cleanup. */
export const removeConsumerAuction = async (
  params: RemoveConsumerAuctionParams
): Promise<void> => {
  const {
    auctionId,
    queryClient,
    setUserAuctionsBadges,
    clearAllAuctionState
  } = params;

  try {
    await removeAuction(auctionId);
  } catch (error) {
    if (!isAuctionAlreadyRemovedError(error)) {
      throw error;
    }
  }

  await clearConsumerDraftAfterRemoval({
    queryClient,
    deletedAuctionId: auctionId,
    setUserAuctionsBadges,
    clearAllAuctionState,
    clearWizardState: params.clearWizardState
  });
};

export const useRemoveConsumerAuction = () => {
  const queryClient = useQueryClient();
  const setUserAuctionsBadges = useSetAtom(userAuctionsBadgesAtom);
  const { clearAllAuctionState } = useClearAuctionState();

  const removeConsumerAuctionFromClient = async (
    auctionId: number,
    options?: { clearWizardState?: boolean }
  ) => {
    await removeConsumerAuction({
      auctionId,
      queryClient,
      setUserAuctionsBadges,
      clearAllAuctionState,
      clearWizardState: options?.clearWizardState
    });
  };

  return {
    removeConsumerAuction: removeConsumerAuctionFromClient
  };
};
